import { Component, Inject, OnInit, Optional } from '@angular/core';
import { Router } from '@angular/router';
import {
	MAT_DIALOG_DATA,
	MatDialogModule,
	MatDialogRef,
} from '@angular/material/dialog';
import { OtpDialogData, OTPResponse } from '../../core/models/interfaces/otp';
import { SharedModule } from '../../shared/shared.module';
import { AuthServiceService } from '../../core/services/authService/auth-service.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { AuthRedirectService } from '@edtech-ui/core/services/auth-redirect/auth-redirect.service';
import { Roles } from '@edtech-ui/constants/roles.enum';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';

@Component({
	selector: 'app-user-registration-otp',
	standalone: true,
	imports: [MatDialogModule, SharedModule],
	templateUrl: './user-registration-otp.component.html',
	styleUrl: './user-registration-otp.component.scss',
})
export class UserRegistrationOtpComponent implements OnInit {

	otpArray = new Array(6).fill(0);
	otpValues: string[] = new Array(6).fill('');
	resendOtpEmail: string = '';
	subscription = new Subscription();
	isLoading: boolean = false;
	isSendingOtp: boolean = false;

	constructor(
		private router: Router,
		private _authService: AuthServiceService,
		@Optional()
		private dialogRef: MatDialogRef<UserRegistrationOtpComponent> | null,
		@Optional() @Inject(MAT_DIALOG_DATA) public data: OtpDialogData | undefined,
		private _toastr: ToastrService,
		private _authRedirectService: AuthRedirectService,
	) { }

	ngOnInit(): void {
		this.subscription.add(
			this._authService.currentUserEmail.subscribe((email) => {
				this.resendOtpEmail = email;
			})
		);
		this._authRedirectService.redirectIfLoggedIn();
	}

	handleOtpInput(event: any, index: number) {
		const input = event.target as HTMLInputElement;
		const value = input.value;

		this.otpValues[index] = value;

		if (value.length === 1 && index < this.otpArray.length - 1) {
			const nextInput = input.nextElementSibling as HTMLInputElement;
			if (nextInput) nextInput.focus();
		} else if (value.length === 0 && index > 0) {
			const prevInput = input.previousElementSibling as HTMLInputElement;
			if (prevInput) prevInput.focus();
		}
	}

	isOtpFilled(): boolean {
		return this.otpValues.join('').length === 6;
	}

	getOtpCode(): string {
		return this.otpValues.join('');
	}

	verifyOtp() {
		// Fix 1: Guard — drop any click that arrives while a request is already in-flight.
		// This prevents multiple API calls from rapid/repeated button clicks.
		if (this.isLoading) return;

		const otp = this.getOtpCode();

		if (otp.length === 6 && /^\d{6}$/.test(otp)) {
			// Fix 2: Set isLoading=true immediately before the API branch so the button
			// is disabled and the spinner shows before any network call is made.
			this.isLoading = true;

			if (this.data?.message === 'signup') {
				const otpNumber = Number(this.getOtpCode());
				const data = {
					email: this._authService.currentUserEmail.getValue(),
					otp: otpNumber,
				};
				this.subscription.add(
					this._authService.postVerifyOTP(data).subscribe({
						next: (response: any) => {
							if (response?.Success) {
								this.isLoading = false;
								this._authService.setToken(response.Data.token);
								this._authService.setUserId(response.Data.user_id);
								this._authService.currentUserId.next(response.Data.user_id);
								localStorage.setItem('refresh_token', response.Data.refresh_token);

								if (response.Data.modules) {
									this._authService.setModules(response.Data.modules);
								}

								this._toastr.success('OTP Verified Successfully');
								this.closeDialog();

								this._authRedirectService.handlePostLoginRedirect(this.data?.redirectUrl);
							} else {
								this._toastr.error(response.Message || 'Invalid OTP');
								this.otpValues.fill('');
								this.isLoading = false;
							}
						},
						error: () => {
							this._toastr.error('Verification failed. Please try again.');
							this.otpValues.fill('');
							this.isLoading = false;
						}
					})
				);
			} else if (this.data?.message === 'forgot') {
				const otpNumber = Number(this.getOtpCode());
				const data = {
					email: this._authService.currentUserEmail.getValue(),
					otp: otpNumber,
				};
				this.subscription.add(
					this._authService.postVerifyResetOtp(data).subscribe({
						next: (response: any) => {
							if (response?.Success) {
								this.isLoading = false;
								this._toastr.success('OTP Verified');
								this._authService.resetToken.next(response.Data.reset_token);
								this.closeDialog();
								this.router.navigate(['/newpassword']);
							} else {
								this._toastr.error(response.Message || 'Invalid OTP');
								this.otpValues.fill('');
								this.isLoading = false;
							}
						},
						// Fix 3: Added missing error handler — previously a network failure
						// in the forgot flow left isLoading=true forever (frozen spinner).
						error: () => {
							this._toastr.error('Verification failed. Please try again.');
							this.otpValues.fill('');
							this.isLoading = false;
						}
					}
					)
				);
			} else {
				// Unknown flow — reset loading state
				this.isLoading = false;
			}
		} else {
			this._toastr.error('Invalid OTP format');
			this.otpValues.fill('');
			this.isLoading = false;
		}
	}

	closeDialog(): void {
		this.dialogRef?.close();
	}

	resendOtp() {
		this.isSendingOtp = true;
		this.subscription.add(
			this._authService.postResendOtp(this.resendOtpEmail).subscribe({
				next: (response: OTPResponse) => {
					if (response?.Success) {
						this.isSendingOtp = false;
						this._toastr.success(response.Message || 'OTP sent successfully');
					} else {
						this._toastr.error(response.Message || 'Failed to resend OTP');
					}
				},
				error: () => {
					this.isSendingOtp = false;
					this._toastr.error('Something went wrong while resending OTP');
				}
			})
		);
	}

	handleKeyDown(event: KeyboardEvent, index: number) {
		if (event.key === 'Backspace' && !this.otpValues[index] && index > 0) {
			const prevInput = (event.target as HTMLInputElement).previousElementSibling as HTMLInputElement;
			if (prevInput) {
				prevInput.focus();
			}
		}
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}
